interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Crops an image based on pixel coordinates relative to the displayed image.
 * @param imageSrc - Source URL of the image
 * @param pixelCrop - Crop area in pixels (relative to displayed size)
 * @param rotation - Rotation in degrees
 * @param scaleX - Ratio of natural width to displayed width
 * @param scaleY - Ratio of natural height to displayed height
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: CropArea,
  rotation = 0,
  scaleX = 1,
  scaleY = 1
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Scale crop coordinates from displayed size to natural size
  const scaledCrop = {
    x: pixelCrop.x * scaleX,
    y: pixelCrop.y * scaleY,
    width: pixelCrop.width * scaleX,
    height: pixelCrop.height * scaleY,
  };

  // For rotation, we need a larger canvas to fit the rotated image
  const rotRad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));

  // Calculate the bounding box of the rotated image
  const rotatedWidth = image.width * cos + image.height * sin;
  const rotatedHeight = image.width * sin + image.height * cos;

  // Set canvas size to the final crop dimensions
  canvas.width = scaledCrop.width;
  canvas.height = scaledCrop.height;

  // Calculate the center offset for rotation
  const cx = rotatedWidth / 2;
  const cy = rotatedHeight / 2;

  // Move to where the crop starts, accounting for rotation
  ctx.save();
  
  // Translate so the crop area is at (0,0)
  ctx.translate(-scaledCrop.x, -scaledCrop.y);
  
  // Move to center of rotated bounds
  ctx.translate(cx, cy);
  
  // Apply rotation
  ctx.rotate(rotRad);
  
  // Draw image centered
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  
  ctx.restore();

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      },
      'image/jpeg',
      0.9
    );
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}
